import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ChangePasswordInput, LoginInput, UserSummary } from 'used-vehicle-contracts';
import { authTokenStore } from '../apis/auth-token';
import { orpcClient } from '../apis/orpc-client';

export interface AuthContextValue {
  user: UserSummary | null;
  isAuthenticated: boolean;
  /** True until the boot-time `auth.me` call settles, so routes do not redirect prematurely. */
  isBootstrapping: boolean;
  /** Set by an administrator's password reset; `/change-password` is forced until it clears. */
  mustChangePassword: boolean;
  isAdmin: boolean;
  login: (input: LoginInput) => Promise<UserSummary>;
  changePassword: (input: ChangePasswordInput) => Promise<UserSummary>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserSummary | null>(() => authTokenStore.getUser());
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => authTokenStore.hasSession());

  // On boot, a stored token is only a hint — confirm it with the service before trusting it. A merely
  // expired access token is renewed silently by the client, so this only fails on a real revocation.
  useEffect(() => {
    if (!authTokenStore.hasSession()) {
      setIsBootstrapping(false);

      return;
    }

    let isCancelled = false;

    const verifySession = async (): Promise<void> => {
      try {
        const me = await orpcClient.auth.me();

        if (isCancelled) {
          return;
        }

        authTokenStore.setUser(me);
        setUser(me);
      } catch {
        if (isCancelled) {
          return;
        }

        authTokenStore.clear();
        setUser(null);
      } finally {
        if (!isCancelled) {
          setIsBootstrapping(false);
        }
      }
    };

    void verifySession();

    return () => {
      isCancelled = true;
    };
  }, []);

  const login = useCallback(
    async (input: LoginInput): Promise<UserSummary> => {
      const session = await orpcClient.auth.login(input);

      authTokenStore.setSession(session);
      setUser(session.user);
      setIsBootstrapping(false);
      await queryClient.invalidateQueries();

      return session.user;
    },
    [queryClient],
  );

  const changePassword = useCallback(
    async (input: ChangePasswordInput): Promise<UserSummary> => {
      // The change bumps the token version, so the pair this page arrived with is already refused: the
      // whole session has to be replaced with the one the service hands back, not just the user record
      // (plan §11 fix 2).
      const session = await orpcClient.auth.changePassword(input);

      authTokenStore.setSession(session);
      setUser(session.user);
      // Everything the workspace asked for while the one-time password was still in force was answered
      // with a 403, so the cache holds nothing worth keeping.
      await queryClient.invalidateQueries();

      return session.user;
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    authTokenStore.clear();
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isBootstrapping,
      mustChangePassword: user?.mustChangePassword ?? false,
      isAdmin: user?.role === 'ADMIN',
      login,
      changePassword,
      logout,
    }),
    [user, isBootstrapping, login, changePassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }

  return context;
};
